/**
 * Admin — Health/Services (Wave 1b W1b-18)
 *
 * Per-service detail page with status / version / rolling latency window
 * (p50/p95/p99) + maintenance scheduling + honest restart/log-tail states.
 *
 * URL:    /admin/health/services?name=<service>
 * Default: ?name=argusai-api when nothing is selected.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  RefreshCw, AlertTriangle, CheckCircle2, XCircle, Wrench,
  FileText,
} from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { relativeTime } from '@/lib/format';
import { apiClient } from '@/lib/api-client';
import { queryClient } from '@/lib/query-client';

const SERVICE_NAMES = [
  'argusai-api', 'argusai-worker', 'postgres', 'valkey', 'qdrant',
  'ollama', 'caddy', 'reranker', 'faithfulness', 'vllm',
  'timescaledb', 'prometheus', 'loki',
] as const;

const SearchSchema = z.object({
  name: z.string().optional(),
});

export const Route = createFileRoute('/admin/health/services')({
  validateSearch: (search) => SearchSchema.parse(search),
  component: HealthServicesPage,
});

// ─── Types ──────────────────────────────────────────────────────────────

type ServiceStatus = 'healthy' | 'degraded' | 'down' | 'unknown';

interface ServiceListEntry {
  name: string;
  status: ServiceStatus;
  latencyMs: number | null;
  lastProbedAt: string | null;
  version: string | null;
  inMaintenance: boolean;
  maintenanceReason: string | null;
}

interface ServiceDetail {
  name: string;
  status: ServiceStatus;
  latencyMs: number | null;
  version: string | null;
  lastProbedAt: string | null;
  errorMessage: string | null;
  rollingWindow: {
    size: number;
    p50: number | null;
    p95: number | null;
    p99: number | null;
  };
  recentProbes: Array<{
    status: ServiceStatus;
    latencyMs: number | null;
    probedAt: string;
  }>;
  inMaintenance: boolean;
  maintenanceWindow: {
    id: string;
    reason: string;
    startedAt: string;
    expiresAt: string;
  } | null;
}

// ─── Page ───────────────────────────────────────────────────────────────

function HealthServicesPage() {
  const navigate = useNavigate({ from: '/admin/health/services' });
  const search = Route.useSearch();
  const selectedName = search.name ?? 'argusai-api';

  const listQuery = useQuery<{ services: ServiceListEntry[] }>({
    queryKey: ['admin', 'health', 'services'],
    queryFn: () =>
      apiClient.get<{ services: ServiceListEntry[] }>('/admin/health/services'),
  });

  const detailQuery = useQuery<ServiceDetail>({
    queryKey: ['admin', 'health', 'services', selectedName],
    queryFn: () =>
      apiClient.get<ServiceDetail>(
        `/admin/health/services/${encodeURIComponent(selectedName)}`,
      ),
  });

  const [maintOpen, setMaintOpen] = useState(false);
  const [maintReason, setMaintReason] = useState('');
  const [maintMinutes, setMaintMinutes] = useState('30');

  const scheduleMaintMutation = useMutation({
    mutationFn: (body: { reason: string; durationMinutes: number }) =>
      apiClient.post(
        `/admin/health/services/${encodeURIComponent(selectedName)}/maintenance`,
        body,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'health', 'services'],
      });
      toast.success('Maintenance window scheduled');
      setMaintOpen(false);
      setMaintReason('');
      setMaintMinutes('30');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Schedule failed';
      toast.error(`Schedule failed: ${message}`);
    },
  });

  const endMaintMutation = useMutation({
    mutationFn: (windowId: string) =>
      apiClient.delete(
        `/admin/health/services/${encodeURIComponent(selectedName)}/maintenance/${windowId}`,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'health', 'services'],
      });
      toast.success('Maintenance window ended');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'End failed';
      toast.error(`End failed: ${message}`);
    },
  });

  const detail = detailQuery.data;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="health-services">
      {/* Top selector */}
      <div className="flex items-center gap-3">
        <Label htmlFor="service-select" className="text-xs">
          Service
        </Label>
        <Select
          value={selectedName}
          onValueChange={(v) =>
            navigate({ search: { name: v } as never })
          }
        >
          <SelectTrigger
            id="service-select"
            className="w-64 h-8 text-xs"
            data-testid="service-selector"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_NAMES.map((n) => (
              <SelectItem key={n} value={n}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {listQuery.data?.services.find((s) => s.name === selectedName)
          ?.inMaintenance && (
          <Badge
            variant="outline"
            className="text-[10px] border-blue-500 text-blue-600 dark:text-blue-400"
          >
            <Wrench className="h-2.5 w-2.5 mr-1" />
            In maintenance
          </Badge>
        )}

        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => setMaintOpen(true)}
            disabled={detail?.inMaintenance}
            title={
              detail?.inMaintenance
                ? 'Already in maintenance'
                : 'Schedule a maintenance window'
            }
            data-testid="maint-schedule"
          >
            <Wrench className="h-3 w-3 mr-1" />
            Schedule maintenance
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled
            title="Service restart is not wired in V1; use ai1 operations runbooks."
            data-testid="restart-button"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Restart unavailable
          </Button>
        </div>
      </div>

      {/* Detail panel */}
      {detailQuery.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading detail...</div>
      ) : detail ? (
        <div className="grid grid-cols-5 gap-3" data-testid="metric-cards">
          <MetricCard
            label="Status"
            value={
              <span
                className={cn(
                  'capitalize',
                  detail.status === 'healthy'
                    && 'text-emerald-600 dark:text-emerald-400',
                  detail.status === 'degraded'
                    && 'text-amber-600 dark:text-amber-400',
                  detail.status === 'down'
                    && 'text-red-600 dark:text-red-400',
                )}
              >
                {detail.inMaintenance ? 'maintenance' : detail.status}
              </span>
            }
          />
          <MetricCard
            label="Latency"
            value={
              detail.latencyMs != null ? (
                <span className="tabular-nums">{detail.latencyMs}ms</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )
            }
          />
          <MetricCard
            label="p50 / p95 / p99"
            value={
              <span className="tabular-nums text-sm">
                {fmtMs(detail.rollingWindow.p50)} /{' '}
                {fmtMs(detail.rollingWindow.p95)} /{' '}
                {fmtMs(detail.rollingWindow.p99)}
              </span>
            }
          />
          <MetricCard
            label="Version"
            value={
              detail.version ? (
                <span className="font-mono text-sm">v{detail.version}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )
            }
          />
          <MetricCard
            label="Last probe"
            value={
              detail.lastProbedAt ? (
                <span className="text-sm">
                  {relativeTime(detail.lastProbedAt)}
                </span>
              ) : (
                <span className="text-muted-foreground">never</span>
              )
            }
          />
        </div>
      ) : (
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">
              Service not found.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Maintenance window banner */}
      {detail?.maintenanceWindow && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="p-3 flex items-center gap-3">
            <Wrench className="h-4 w-4 text-blue-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium">
                Maintenance window active
              </div>
              <div className="text-[11px] text-muted-foreground">
                {detail.maintenanceWindow.reason} · expires{' '}
                {relativeTime(detail.maintenanceWindow.expiresAt)}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              onClick={() =>
                detail.maintenanceWindow &&
                endMaintMutation.mutate(detail.maintenanceWindow.id)
              }
              disabled={endMaintMutation.isPending}
              title="End maintenance early (owner only)"
              data-testid="maint-end"
            >
              End early
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recent probes table */}
      {detail && detail.recentProbes.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-xs font-semibold mb-2">Recent probes</div>
            <div className="space-y-1">
              {detail.recentProbes.map((p, i) => {
                const Icon =
                  p.status === 'healthy' ? CheckCircle2
                  : p.status === 'degraded' ? AlertTriangle
                  : p.status === 'down' ? XCircle
                  : RefreshCw;
                const iconColor =
                  p.status === 'healthy' ? 'text-emerald-500'
                  : p.status === 'degraded' ? 'text-amber-500'
                  : p.status === 'down' ? 'text-red-500'
                  : 'text-muted-foreground';
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-[11px]"
                  >
                    <Icon className={cn('h-3 w-3 shrink-0', iconColor)} />
                    <span className="capitalize w-20">{p.status}</span>
                    <span className="tabular-nums w-20">
                      {p.latencyMs != null ? `${p.latencyMs}ms` : '—'}
                    </span>
                    <span className="text-muted-foreground tabular-nums ml-auto">
                      {relativeTime(p.probedAt)}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live log tailer */}
      <LogTailer />

      {/* Schedule maintenance dialog */}
      <Dialog open={maintOpen} onOpenChange={setMaintOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule maintenance — {selectedName}</DialogTitle>
            <DialogDescription>
              Flag this service as expected to be down for the duration. The
              Overview banner stays green during the window.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="maint-reason" className="text-xs">
                Reason
              </Label>
              <Textarea
                id="maint-reason"
                value={maintReason}
                onChange={(e) => setMaintReason(e.target.value)}
                placeholder="e.g. weekly Postgres reindex"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="maint-duration" className="text-xs">
                Duration (minutes, 5 to 1440)
              </Label>
              <Input
                id="maint-duration"
                type="number"
                min={5}
                max={1440}
                value={maintMinutes}
                onChange={(e) => setMaintMinutes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMaintOpen(false)}
              disabled={scheduleMaintMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const minutes = parseInt(maintMinutes, 10);
                if (Number.isNaN(minutes) || minutes < 5 || minutes > 1440) {
                  toast.error('Duration must be between 5 and 1440 minutes');
                  return;
                }
                if (maintReason.trim().length < 3) {
                  toast.error('Reason must be at least 3 characters');
                  return;
                }
                scheduleMaintMutation.mutate({
                  reason: maintReason.trim(),
                  durationMinutes: minutes,
                });
              }}
              disabled={scheduleMaintMutation.isPending}
            >
              {scheduleMaintMutation.isPending
                ? 'Scheduling...'
                : 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function MetricCard({
  label, value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
          {label}
        </div>
        <div className="text-base font-semibold mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function fmtMs(v: number | null): string {
  return v == null ? '—' : `${v}ms`;
}

/**
 * Honest V1 log-tail state. The backend now returns NOT_IMPLEMENTED instead
 * of a synthetic stream, so the UI keeps the surface visible but disabled.
 */
function LogTailer() {
  return (
    <Card data-testid="log-tailer">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
          <div className="text-xs font-semibold">Live log tail</div>
          <Badge variant="outline" className="text-[10px]">
            not wired in V1
          </Badge>
          <div className="ml-auto">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              disabled
              data-testid="log-start"
            >
              Tail logs unavailable
            </Button>
          </div>
        </div>
        <div
          className="font-mono text-[10px] bg-muted/30 rounded p-2 h-48 overflow-y-auto space-y-0.5"
          data-testid="log-output"
        >
          <div
            className="text-muted-foreground flex flex-col gap-1"
            data-testid="log-not-implemented-empty"
          >
            <div className="font-semibold uppercase tracking-wide text-[9px]">
              Log tail unavailable in V1
            </div>
            <div>
              Docker log tailing is not connected to the tenant UI yet. No
              synthetic log lines are shown here.
            </div>
            <div className="opacity-80">
              Use the ai1 operations runbook for live service logs until this
              surface is wired.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
